import { useCurrentFrame, useVideoConfig } from 'remotion';
import { useMemo } from 'react';
import { getSpeakerLayoutBorderKey } from '@goldcast/utils';
import { CompositeSegment } from '../../MainPlayer/types/Segment/CompositeSegment';
import AudiogramComponent from '../AudiogramComponent/AudiogramComponent';
import VideoInCompositeSegment from './VideoInCompositeSegment';
import { layoutConfigMap } from './layoutConfigMap';
import { getAnimationStylesForFrame } from '@/App/remotion/MainPlayer/utils/animation/getAnimationStylesForFrame';
import { roundToNDecimalPlaces } from '@/libs/utils';
import { isLayoutAudiogram } from '@/libs/layout';

const CompositeSegmentComponent: React.FC<{
  segment: CompositeSegment;
  canEditSpeakerLabel: boolean;
  canManuallyReposition?: boolean;
  preventOverlappingWithOtherCrops?: boolean;
}> = ({ segment, canEditSpeakerLabel, canManuallyReposition = false, preventOverlappingWithOtherCrops }) => {
  const currentFrame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const layoutDetailsAtCurrentFrame = segment.layoutChanges.find(change => {
    return currentFrame >= change.fromFrame && currentFrame <= change.toFrame;
  });

  const commonStartFromForElements = useMemo(() => {
    // Reference - https://www.remotion.dev/docs/miscellaneous/snippets/jumpcuts
    const firstVideo = segment.videos[0];
    if (!firstVideo) {
      return null;
    }

    const fromFrame = firstVideo.fromFrame;
    if (!segment.playRanges.length) {
      return fromFrame;
    }

    let summedUpDurations = 0;
    for (const section of segment.playRanges) {
      summedUpDurations += section.toFrame - section.fromFrame;
      if (summedUpDurations > currentFrame) {
        return roundToNDecimalPlaces(section.toFrame - summedUpDurations, 3);
      }
    }

    return fromFrame;
  }, [currentFrame]);

  const backgroundStyle = useMemo(() => {
    const background = segment.details.background;
    if (background.type === 'IMAGE_URL') {
      return { backgroundImage: `url(${background.value})`, backgroundSize: 'cover', backgroundPosition: 'center' };
    }

    // For type === 'COLOR' or others
    return { backgroundColor: background.value };
  }, [segment.details?.background?.value]);

  const videosToShow: string[] = useMemo(() => {
    if (!layoutDetailsAtCurrentFrame) {
      return [];
    }

    const videoIds = layoutDetailsAtCurrentFrame.videoIds;

    return segment.videos
      .map(video => video.id)
      .filter(videoId => videoIds.includes(videoId))
      .sort((a, b) => videoIds.indexOf(a) - videoIds.indexOf(b));
  }, [layoutDetailsAtCurrentFrame?.videoIds, layoutDetailsAtCurrentFrame?.layout, segment.videos]);

  // Pre-compute which layout changes introduce new videos (only recalculates when layout changes, not every frame)
  const layoutChangesWithNewVideos = useMemo(() => {
    const previousVideoIds = new Set<string>();
    const framesWithNewVideos: number[] = [];

    for (let i = 0; i < segment.layoutChanges.length; i++) {
      const layoutChange = segment.layoutChanges[i];

      const hasNewVideos = layoutChange.videoIds.some(id => !previousVideoIds.has(id));

      if (hasNewVideos) {
        framesWithNewVideos.push(layoutChange.fromFrame);
      }

      layoutChange.videoIds.forEach(id => previousVideoIds.add(id));
    }

    return framesWithNewVideos;
  }, [segment.layoutChanges]);

  // Find when the current speaker was introduced to restart their label animation from that frame
  const mostRecentLayoutChangeFrame = useMemo(() => {
    if (currentFrame === 0 || !layoutDetailsAtCurrentFrame) {
      return 0;
    }

    // Look backwards through layout changes to find the most recent one that added new videos
    for (let i = layoutChangesWithNewVideos.length - 1; i >= 0; i--) {
      const frame = layoutChangesWithNewVideos[i];
      if (frame <= currentFrame) {
        return frame;
      }
    }

    return 0;
  }, [currentFrame, layoutDetailsAtCurrentFrame, layoutChangesWithNewVideos]);

  // Use pre-computed playback-adjusted effects from player config
  const playableEffects = segment.playbackAdjustedEffects || [];

  const finalConfig = useMemo(() => {
    // If making changes here please update the getFinalConfigForLayout function in layoutUtils.ts
    if (!layoutDetailsAtCurrentFrame) {
      return [];
    }
    if (isLayoutAudiogram(layoutDetailsAtCurrentFrame.layout)) {
      return [];
    }
    const config =
      layoutConfigMap[layoutDetailsAtCurrentFrame.size]?.[layoutDetailsAtCurrentFrame.layout]?.[
        layoutDetailsAtCurrentFrame.fitMode
      ]?.[getSpeakerLayoutBorderKey(!layoutDetailsAtCurrentFrame.hideBorders)]?.[videosToShow.length];

    if (config) {
      return config;
    }
    return [
      {
        style: { display: 'none' },
        containerStyle: { display: 'none' }
      }
    ];
  }, [layoutDetailsAtCurrentFrame, videosToShow]);

  if (commonStartFromForElements === null) {
    return null;
  }

  if (layoutDetailsAtCurrentFrame?.layout === 'AUDIOGRAM') {
    return (
      <AudiogramComponent
        backgroundStyle={backgroundStyle}
        layoutDetailsAtCurrentFrame={layoutDetailsAtCurrentFrame}
        segment={segment}
        canEditSpeakerLabel={canEditSpeakerLabel}
        commonStartFromForElements={commonStartFromForElements}
        finalConfig={finalConfig}
      />
    );
  }

  const { style: animationStyle, effectBounds } = getAnimationStylesForFrame(currentFrame, playableEffects, fps);

  const shouldApplyAnimationToIndividualVideo =
    layoutDetailsAtCurrentFrame &&
    effectBounds &&
    effectBounds.fromFrame >= layoutDetailsAtCurrentFrame.fromFrame &&
    effectBounds.toFrame <= layoutDetailsAtCurrentFrame.toFrame &&
    videosToShow.length === 1;

  return (
    <div
      data-testid="composite-segment-preview"
      style={{
        position: 'relative',
        height: '100%',
        width: '100%',
        ...backgroundStyle,
        ...(shouldApplyAnimationToIndividualVideo ? {} : animationStyle)
      }}
    >
      {segment.videos.map(video => {
        const videoIndex = videosToShow.indexOf(video.id);
        const muted = (layoutDetailsAtCurrentFrame?.mutedVideosId ?? []).includes(video.id);
        const isInLayout = videosToShow.includes(video.id);

        // Hide speaker label for pip layout for bottom right video
        const isPIPLayoutEnabled = layoutDetailsAtCurrentFrame?.layout === 'PIP';
        const isPipBubble = videoIndex === 1 && isPIPLayoutEnabled;
        const speakerDetails = isPipBubble ? undefined : video.speakerDetails;

        return (
          <VideoInCompositeSegment
            key={video.id}
            video={{ ...video, speakerDetails }}
            videoIndex={videoIndex}
            finalConfig={finalConfig}
            compositeSegmentId={segment.id}
            hasScreenshareAtCurrentFrame={!!layoutDetailsAtCurrentFrame?.hasScreenshare}
            commonStartFromForElements={commonStartFromForElements}
            canEditSpeakerLabel={canEditSpeakerLabel}
            // This is done cause video not in scene are still rendered.
            canManuallyReposition={canManuallyReposition && videoIndex !== -1}
            cropInformation={layoutDetailsAtCurrentFrame?.videoCropPositionMap?.[video.id] || null}
            cropStyles={layoutDetailsAtCurrentFrame?.videoCropPositionStyles?.[video.id] || undefined}
            allCropInformation={layoutDetailsAtCurrentFrame?.videoCropPositionMap}
            muted={muted}
            layoutChangeFrame={mostRecentLayoutChangeFrame}
            preventOverlappingWithOtherCrops={preventOverlappingWithOtherCrops}
            animationStyles={shouldApplyAnimationToIndividualVideo && isInLayout ? animationStyle : {}}
          />
        );
      })}
    </div>
  );
};

export default CompositeSegmentComponent;
